import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, styled, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useRegisterActorMutation } from "Auth/Api/Mutations/useRegisterActorMutation";
import { BlFormCheckbox } from "Forms/Components/BlFormCheckbox";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlPhoneInput } from "Forms/Components/BlPhoneInput";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlButton } from "Shared/Buttons/BlButton";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import { isPhoneValid } from "Shared/Utils/validations";

const RegisterActorSchema = z.object({
  firstName: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  lastName: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  company: z.string().optional(),
  login: z.email({
    error: () => getTranslation(Resources.Validation.InvalidEmail),
  }),
  phone: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) })
    .refine(isPhoneValid, {
      error: () => getTranslation(Resources.Validation.InvalidPhone),
    }),
  termsAccepted: z.literal(true, {
    error: () => getTranslation(Resources.Validation.IsTrue),
  }),
});

type RegisterActorFormData = z.infer<typeof RegisterActorSchema>;

const Form = styled("form")`
  width: 100%;
  max-width: 400px;
`;

export const RegisterActorForm = (): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();
  const { mutate, error, isSuccess } = useRegisterActorMutation();

  const form = useForm<RegisterActorFormData>({
    resolver: zodResolver(RegisterActorSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      login: "",
      phone: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  useHandleServerError({
    form,
    error,
    formKey: "login",
    resource: Resources.RegisterActor.Error,
  });

  const submit = (data: RegisterActorFormData) => {
    mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company || null,
      login: data.login,
      phone: data.phone,
    });
  };

  if (isSuccess) {
    return (
      <Box sx={{ width: "100%", maxWidth: "400px" }}>
        <Typography variant="h1" mb={Spacing.M}>
          {t(Resources.RegisterActor.Success.Title)}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.text.secondary}
          mb={Spacing.L}
        >
          {t(Resources.RegisterActor.Success.Description)}
        </Typography>
        <UnstyledLink to={AppRouting.SignIn.path()} isUnderlined>
          <Typography variant="body2">{t(Resources.SignIn.Button)}</Typography>
        </UnstyledLink>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "400px" }}>
      <Typography variant="h1" mb={Spacing.S}>
        {t(Resources.RegisterActor.Title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={Spacing.L}>
        {t(Resources.RegisterActor.Subtitle)}
      </Typography>
      <Form onSubmit={handleSubmit(submit)}>
        <Stack spacing={Spacing.L}>
          <Stack spacing={Spacing.S}>
            <BlFormInput
              control={control}
              name="firstName"
              errors={errors}
              label={t(Resources.RegisterActor.FirstName.Label)}
              placeholder={t(Resources.RegisterActor.FirstName.Label)}
            />
            <BlFormInput
              control={control}
              name="lastName"
              errors={errors}
              label={t(Resources.RegisterActor.LastName.Label)}
              placeholder={t(Resources.RegisterActor.LastName.Label)}
            />
            <BlFormInput
              control={control}
              name="company"
              errors={errors}
              label={t(Resources.RegisterActor.Company.Label)}
              placeholder={t(Resources.RegisterActor.Company.Label)}
            />
            <Box sx={{ height: Spacing.XXXL }} />
            <BlFormInput
              control={control}
              name="login"
              errors={errors}
              type="email"
              autoComplete="email"
              label={t(Resources.RegisterActor.Email.Label)}
              placeholder={t(Resources.RegisterActor.Email.Label)}
            />
            <BlPhoneInput
              control={control}
              name="phone"
              errors={errors}
              label={t(Resources.RegisterActor.Phone.Label)}
              placeholder={t(Resources.RegisterActor.Phone.Label)}
            />
            <BlFormCheckbox
              control={control}
              name="termsAccepted"
              errors={errors}
              label={
                <Typography variant="body2" component="span">
                  {t(Resources.RegisterActor.TermsAndConditions)}{" "}
                  <UnstyledLink to="" isUnderlined>
                    {t(Resources.RegisterActor.TermsLink)}
                  </UnstyledLink>
                  .
                </Typography>
              }
            />
          </Stack>
          <Stack spacing={Spacing.XS}>
            <BlButton type="submit" fullWidth color="primary">
              {t(Resources.RegisterActor.Button)}
            </BlButton>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography display="inline-block" variant="body2">
                {t(Resources.RegisterActor.AlreadyHaveAccount)}
              </Typography>
              &nbsp;
              <UnstyledLink to={AppRouting.SignIn.path()} isUnderlined>
                <Typography variant="body2">
                  {t(Resources.RegisterActor.SignInNow)}
                </Typography>
              </UnstyledLink>
            </Box>
          </Stack>
        </Stack>
      </Form>
    </Box>
  );
};
